'use client'
import React, { useState, useEffect, useRef, useImperativeHandle } from 'react'
import { cn } from '../../helpers/utils.js'
import { Tabs, TabsList, TabsTrigger } from '../../components/tabs.js'
import Title from '../Title/index.js'
import SwiperBox from '../SwiperBox/index.js'
import { withLayout } from '../../shared/Styles.js'
import { gaTrack } from '../../shared/track.js'
import { useMediaQuery } from 'react-responsive'
import { useRollout } from '../../hooks/useRollout.js'
import { ShelfDisplayWrapItem, ShelfDisplayHorizontalItem } from './shelfDisplayItem.js'
import type { ShelfDisplayProps, ShelfDisplayItem } from './shelfDisplay.js'

const componentType = 'image'
const componentName = 'product_shelf'

const sanitizeCssSelector = (value?: string | number) => {
  if (value === undefined || value === null) return 'default'
  const sanitized = String(value).replace(/[^a-zA-Z0-9_-]/g, '')
  return sanitized || 'default'
}

const createInstanceId = () => `shelf-display-${Math.random().toString(36).slice(2, 9)}`

const ShelfDisplay = React.forwardRef<HTMLDivElement, ShelfDisplayProps>(
  (
    {
      key,
      data,
      event,
      buildData,
      breakpoints,
      className = '',
      recommendedData,
      target = '_self',
      metafields,
      isDisplayGudgments = false,
      isDisplayBackImage = false,
      onAddCart,
      onBuyNow,
      onLearnMore,
      ...rest
    },
    ref
  ) => {
    const {
      productsTab = [],
      productsCard = [],
      title,
      isShowTab = true,
      tabShape = 'square',
      isShowTag = false,
      isShowOriginalPrice = true,
      isShowRecommendedCard = false,
      ...other
    } = data

    const [tabId, setTabId] = useState<string>('')
    const [currentItems, setCurrentItems] = useState<ShelfDisplayItem[]>([])

    const isView = useRef<boolean>(false)
    const isRecommend = useRef<boolean>(false)
    const innerRef = useRef<HTMLDivElement>(null)
    const instanceIdRef = useRef<string>(createInstanceId())

    const isMobile = useMediaQuery({ query: '(max-width: 768px)' })
    const [viewRef, inView] = useRollout<HTMLDivElement>({ threshold: 0 })

    const isOnce = currentItems?.length <= 1 && isDisplayGudgments
    const isShowGudgments = !isMobile && currentItems?.length <= 2 && isDisplayGudgments

    const showItemLength = (size?: number) => {
      switch (size) {
        case 1440:
          return isShowGudgments ? currentItems?.length : 4
        case 1024:
          return isShowGudgments ? currentItems?.length : 3
        default:
          return isShowGudgments ? currentItems?.length : 2.3
      }
    }

    const gackViewEvent = () => {
      gaTrack({
        event: 'ga4Event',
        event_name: 'view_item_list',
        event_parameters: {
          page_group: 'Home Page',
          item_list_name: 'Home_Page_Bundle',
          items: recommendedData?.map((item, index) => {
            const findData = item?.variants?.find((v: any) => v?.sku === item?.sku)
            const variant = findData || item?.variants?.[0]
            return {
              item_id: item?.sku || variant?.sku,
              item_name: item?.name,
              item_variant: variant?.name,
              price: variant?.price,
              index: index + 1,
            }
          }),
        },
      })
    }

    const handleCurrentTab = (currentData: ShelfDisplayItem[], flag: boolean) => {
      if (flag) {
        const newCurrentData = recommendedData?.map?.(item => {
          return {
            ...item,
            isShowRecommended: true,
          }
        })
        setCurrentItems(newCurrentData?.length ? newCurrentData || [] : [])
      } else {
        const isArray = Array.isArray(currentData)
        if (isArray) {
          const newCurrentData = currentData
            ?.map?.(item => {
              const findData = buildData?.products?.find(params => params?.handle === item?.handle)
              if (findData) {
                return {
                  sku: item.sku,
                  isShowRecommended: false,
                  custom_name: item.custom_name,
                  custom_description: item.custom_description,
                  custom_image: item.custom_image,
                  custom_theme: item.custom_theme,
                  custom_primary_link: item?.custom_primary_link || '',
                  custom_secondary_link: item?.custom_secondary_link || '',
                  ...findData,
                }
              }
            })
            ?.filter(item => item)
          setCurrentItems(newCurrentData?.length ? newCurrentData || [] : [])
        } else {
          setCurrentItems([])
        }
      }
    }

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

    useEffect(() => {
      if (inView && recommendedData?.length && !isView.current) {
        isView.current = true
        gackViewEvent()
      }
    }, [inView, recommendedData])

    // 算法数据会稍微延迟，所以需监听recommendedData，为了防止在有算法数据且对默认渲染第一个后反复触发，用isRecommend关闭
    useEffect(() => {
      if (isRecommend.current) return
      if (!isRecommend.current && recommendedData?.length) {
        isRecommend.current = true
      }
      if (isShowTab) {
        const currentTab = productsTab?.find(item => item?.tab === tabId) || productsTab?.[0]
        setTabId(currentTab?.tab || '')
        handleCurrentTab(currentTab?.data || [], currentTab?.isShowRecommendedTab)
        return
      }
      handleCurrentTab(productsCard, isShowRecommendedCard)
    }, [recommendedData])

    useEffect(() => {
      if (isShowTab) {
        const currentTab = productsTab?.find(item => item?.tab === tabId) || productsTab?.[0]
        handleCurrentTab(currentTab?.data || [], currentTab?.isShowRecommendedTab)
        return
      }
      handleCurrentTab(productsCard, isShowRecommendedCard)
      // buildData 更新时需要重新计算当前列表
    }, [buildData])

    const safeTabKey = sanitizeCssSelector(tabId)
    const swiperInstanceId = `${instanceIdRef.current}-${safeTabKey}`

    return (
      <div
        ref={innerRef}
        {...rest}
        className={cn('shelf-display-wrap text-info-primary w-full', className, {
          'aiui-dark': data?.theme === 'dark',
        })}
      >
        {title && <Title data={{ title: title }} />}
        {isShowTab && productsTab?.length > 0 && (
          <Tabs
            value={tabId}
            onValueChange={value => {
              const currentTab = productsTab?.find(item => item?.tab === value)
              if (currentTab) {
                setTabId(value)
                handleCurrentTab(currentTab.data || [], currentTab.isShowRecommendedTab)
                if (!currentTab.isShowRecommendedTab) {
                  gaTrack({
                    event: 'ga4Event',
                    event_name: 'component_click',
                    event_parameters: {
                      page_group: 'Home Page',
                      component_type: componentType,
                      component_name: componentName,
                      component_title: data?.title,
                      component_position: 1,
                      navigation: value,
                    },
                  })
                  return
                }
                gackViewEvent()
              }
            }}
            shape={tabShape === 'rounded' ? 'rounded' : 'square'}
          >
            <TabsList>
              {productsTab.map(item => (
                <TabsTrigger key={item?.id || item?.tab} value={item?.tab}>
                  {item?.tab || ''}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
        <div
          ref={viewRef as any}
          className="tablet:min-h-[393px] laptop:min-h-[425px] desktop:min-h-[405px] lg-desktop:min-h-[409px]"
        >
          <SwiperBox
            key={swiperInstanceId}
            data={{
              list: currentItems,
              configuration: {
                ...other,
                event: event,
                isShowTag,
                isShowOriginalPrice,
                target: target,
                metafields: metafields,
                itemLength: currentItems?.length,
                isDisplayBackImage: isDisplayBackImage,
                onAddCart,
                onBuyNow,
                onLearnMore,
              },
            }}
            id={swiperInstanceId}
            className={`${isShowTab ? 'mt-6' : ''} shelf-display-swiper-box !overflow-visible`}
            itemClassName={isShowGudgments ? 'flex-1' : ''}
            Slide={isShowGudgments ? ShelfDisplayHorizontalItem : ShelfDisplayWrapItem}
            breakpoints={
              breakpoints || {
                0: {
                  spaceBetween: 12,
                  freeMode: false,
                  slidesPerView: 1,
                },
                374: {
                  spaceBetween: 12,
                  freeMode: false,
                  slidesPerView: isOnce ? 1 : 1.2,
                },
                768: {
                  spaceBetween: isOnce ? 0 : 16,
                  freeMode: false,
                  slidesPerView: showItemLength(),
                },
                1024: {
                  spaceBetween: isOnce ? 0 : 16,
                  freeMode: false,
                  slidesPerView: showItemLength(1024),
                },
                1440: {
                  spaceBetween: isOnce ? 0 : 16,
                  freeMode: false,
                  slidesPerView: showItemLength(1440),
                },
              }
            }
          />
        </div>
      </div>
    )
  }
)

ShelfDisplay.displayName = 'ShelfDisplay'
export default withLayout(ShelfDisplay)
